import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Layout, notification } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Footer from "./Footer";
import HeaderContent from "./HeaderContent";
import { useDispatch } from "react-redux";
import { shallowEqual, useSelector } from "react-redux";
import { toggleLevelList, toggleIdentityList } from "@/store/reducers/appSlice";
import Sider from "./Sider";
import { IndexMemberLevelList, IndexMemberIdentityList } from "@/service";
import { MarketGoodsInventoryWarningList } from "@/service/market";
import styles from "./layout.module.less";

const { Header, Content } = Layout;

const AppLayout = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth, shallowEqual);
  const app = useSelector((state) => state.app, shallowEqual);
  console.log(auth);
  const routes = useMemo(() => props.routes, [props.routes]);
  const [notificationApi, notificationHolder] = notification.useNotification();
  const warningTimerRef = useRef(null);

  // 查找第一个可用的路由
  const findFirstAvailableRoute = (menuList) => {
    for (const menu of menuList) {
      // 跳过隐藏的菜单
      if (menu.hidden) continue;

      // 如果有子路由，递归查找
      if (menu.routes && menu.routes.length > 0) {
        for (const route of menu.routes) {
          if (!route.hidden && route.path) {
            // 如果子路由还有嵌套路由，继续查找最深层的可用路由
            if (route.routes && route.routes.length > 0) {
              const nestedRoute = findFirstAvailableRoute([route]);
              if (nestedRoute) return nestedRoute;
            }
            // 返回第一个可用的子路由
            return route.path;
          }
        }
      }

      // 如果菜单本身就是一个可用路由（没有子路由）
      if (menu.path && !menu.routes?.length) {
        return menu.path;
      }
    }
    return null;
  };

  useEffect(() => {
    // 动态重定向逻辑
    if (
      location.pathname === "/" &&
      auth.permissioned &&
      app.menuList?.length > 0
    ) {
      // 检查用户是否有访问首页的权限
      const hasWorkspaceAccess = app.menuList.some(
        (menu) =>
          menu.path === "/workspace" &&
          !menu.hidden &&
          menu.routes?.some(
            (route) => route.path === "/workspace/dashboard" && !route.hidden
          )
      );
      console.log("hasWorkspaceAccess", hasWorkspaceAccess);
      if (hasWorkspaceAccess) {
        // 有首页权限，按原逻辑重定向
        navigate("/workspace/dashboard", { replace: true });
      } else {
        // 没有首页权限，找到第一个有权限的页面进行重定向
        const firstAvailableRoute = findFirstAvailableRoute(app.menuList);
        if (firstAvailableRoute) {
          navigate(firstAvailableRoute, { replace: true });
        } else {
          // 如果没有任何可用路由，重定向到403页面
          navigate("/forbidden", { replace: true });
        }
      }
    }
  }, [
    location.pathname,
    app.menuList,
    auth.permissioned,
    props.redirect,
    navigate
  ]);

  useEffect(() => {
    const checkWarning = async () => {
      const res = await MarketGoodsInventoryWarningList({
        page: 1,
        per_page: 100
      }).catch((err) => {
        console.log(err);
      });
      const hasWarning =
        (res?.data?.total && res.data.total > 0) ||
        (Array.isArray(res?.data?.data) && res.data.data.length > 0);
      if (hasWarning) {
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const names = list
          .map((item) => item?.name)
          .filter(Boolean)
          .slice(0, 100);
        if (names.length > 0) {
          notificationApi.open({
            message: "库存预警",
            description: `以下商品库存预警：${names.join("、")}`,
            btn: (
              <Button
                type="primary"
                onClick={() => navigate("/market/goods/list")}
              >
                去查看
              </Button>
            )
          });
        }
      }
    };
    if (auth.permissioned) {
      checkWarning();
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current);
      }
      warningTimerRef.current = setInterval(() => {
        checkWarning();
      }, 10 * 60 * 1000);
    }
    return () => {
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current);
        warningTimerRef.current = null;
      }
    };
  }, [auth.permissioned]);

  return (
    <Layout className={styles.layout}>
      {notificationHolder}
      <Sider routes={routes} />
      <div className={styles.main}>
        <Header className={styles.header}>
          <HeaderContent />
        </Header>
        <div className={styles.content}>
          <Content className={styles.pageContent}>
            <div className={styles.pageInner}>
              <Outlet />
            </div>
          </Content>
          <Footer />
        </div>
      </div>
    </Layout>
  );
};

AppLayout.propTypes = {
  routes: PropTypes.array,
  path: PropTypes.string,
  redirect: PropTypes.string,
  store: PropTypes.object
};

export default AppLayout;
